import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, isMock, commitAll } from '../api'
import { useAuth } from '../auth/AuthContext'
import { LEVELS } from '../types'
import { RoleForm } from './RoleForm'
import { formFromRow, formProblem, rowEditBody } from '../lib/role-draft'
import type { RoleFormState } from '../lib/role-draft'
import type { DocSection } from '../types'
import { useToast } from '../components/Toasts'
import { ErrorState } from '../components/States'
import { RowWindow } from '../components/RowWindow'
import { SAMPLE_BULK_CSV, bigSampleCsv } from '../lib/csv'
import { count, rangeShort } from '../lib/format'
import type { BulkRow, UploadJob } from '../types'
import { BackButton } from '../components/BackButton'

const ROW_BASE = 44
const ERROR_LINE = 18
/**
 * Lines reserved for each message under a title, and clamped to in CSS.
 *
 * The row window has to know a row's height before it draws it, which is what
 * lets 5,000 rows scroll. That only works if nothing inside the row can be
 * taller than the arithmetic says. Text that wraps freely can be, and was:
 * a long job title wrapped to three lines in a row that had reserved one, and
 * painted over the row beneath it.
 *
 * So the title is held to one line and everything else is held to two, in CSS,
 * with the full text a click away in the panel. Reserving and clamping the same
 * number is what makes the height a fact rather than an estimate.
 */
const MESSAGE_LINES = 2

/** Below this, an import finishes in one call and progress would just flicker. */
const BATCHED_FROM = 400

/** "a", "a and b", "a, b and c". Joining three things with "and" twice reads badly. */
const list = (items: string[]) =>
  items.length < 3
    ? items.join(' and ')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`

/**
 * A spreadsheet row's exact height, which the row window needs before it draws.
 *
 * Only the spreadsheet path uses the window now. Its rows carry short, known
 * error messages rather than prose, so the height is arithmetic rather than a
 * guess. Document rows lay out normally and let the browser decide.
 */
const heightOf = (r: BulkRow) =>
  ROW_BASE +
  r.errors.length * ERROR_LINE * MESSAGE_LINES +
  (r.sameTitle ? ERROR_LINE * MESSAGE_LINES : 0) +
  (r.guessed?.length ? ERROR_LINE * MESSAGE_LINES : 0)

/**
 * How long to wait for verdicts.
 *
 * A spreadsheet is split and checked in parallel and is done in seconds. A
 * document is read by a model, one call per twenty roles at about half a
 * minute each, so a big one is genuinely minutes rather than seconds.
 */
const CHECK_TIMEOUT_MS = 120_000
const READ_TIMEOUT_MS = 900_000

/** Polls until every row in the file has a verdict, or the job gives up. */
async function waitForRows(
  jobId: string,
  limitMs: number,
  onProgress?: (job: UploadJob) => void,
): Promise<UploadJob> {
  const until = Date.now() + limitMs
  let last: UploadJob | null = null
  while (Date.now() < until) {
    const job = await api.getUpload(jobId)
    last = job
    onProgress?.(job)
    if (job.status === 'failed') {
      throw new Error(job.error ?? 'That file could not be read.')
    }
    const counted = (job.summary?.ready ?? 0) + (job.summary?.errors ?? 0)
    const expected = job.rowCount ?? 0
    if (expected > 0 && counted >= expected) return job
    await new Promise((r) => setTimeout(r, 900))
  }
  if (last?.rows?.length) return last
  throw new Error(
    'Checking this file is taking longer than expected. It may still finish, so try again in a moment.',
  )
}

const summaryLine = (ready: number, flagged: number) =>
  `${count(ready)} ready to import, ${count(flagged)} ${flagged === 1 ? 'needs' : 'need'} attention`

type Mode = 'csv' | 'doc'

const CSV_CELLS: { key: string; label: string; width: string }[] = [
  { key: 'line', label: 'Line', width: '54px' },
  { key: 'title', label: 'Title', width: 'minmax(220px, 2fr)' },
  { key: 'family', label: 'Family', width: 'minmax(130px, 1fr)' },
  { key: 'level', label: 'Level', width: '84px' },
  { key: 'location', label: 'Location', width: 'minmax(120px, 1fr)' },
  { key: 'range', label: 'Range', width: '120px' },
  { key: 'status', label: 'Status', width: '96px' },
  { key: 'verdict', label: '', width: '138px' },
]

// A document never carries a location, a pay range or a status, so those
// columns would be three of empty. Where the role sits in the file earns its
// place instead: it is how a person finds it again.
//
// A PDF has pages and a Word file does not, so the column says whichever is
// true. Calling a position a page would be a number nobody could look up.
const docCells = (paged: boolean): { key: string; label: string; width: string }[] => [
  { key: 'page', label: paged ? 'Page' : 'Order', width: '58px' },
  { key: 'title', label: 'Title', width: 'minmax(220px, 2.4fr)' },
  { key: 'family', label: 'Family', width: 'minmax(140px, 1.1fr)' },
  { key: 'level', label: 'Level', width: '92px' },
  { key: 'industry', label: 'Industry', width: 'minmax(140px, 1.1fr)' },
  { key: 'verdict', label: '', width: '148px' },
]

const DOC_TYPES = /\.(pdf|docx|txt)$/i

export function BulkUpload() {
  const navigate = useNavigate()
  const toast = useToast()
  const { me, refresh } = useAuth()
  const families = me?.settings.jobFamilies ?? []

  const [mode, setMode] = useState<Mode>('csv')
  const [text, setText] = useState('')
  const [fileName, setFileName] = useState('')
  /** The Excel file itself, when that is what was chosen. Sent as bytes. */
  const [sheet, setSheet] = useState<File | null>(null)
  const [docFile, setDocFile] = useState<File | null>(null)
  const [job, setJob] = useState<UploadJob | null>(null)
  const [checking, setChecking] = useState(false)
  const [progress, setProgress] = useState('')
  const [importing, setImporting] = useState(false)
  const [fixing, setFixing] = useState(false)
  const [editing, setEditing] = useState<BulkRow | null>(null)
  const [error, setError] = useState('')

  const rows = job?.rows ?? []
  const ready = job?.summary?.ready ?? 0
  const flagged = job?.summary?.errors ?? 0
  const isDoc = job?.kind === 'docset'
  const paged = rows.some((r) => r.page)
  const workedOut = rows.filter((r) => r.guessed?.length).length
  const missingFamily = rows.filter((r) => !r.family).length
  const missingLevel = rows.filter((r) => !r.level).length
  const cells = isDoc ? docCells(paged) : CSV_CELLS
  const grid = cells.map((c) => c.width).join(' ')

  const reset = () => {
    setJob(null)
    setError('')
    setProgress('')
  }

  const switchTo = (next: Mode) => {
    setMode(next)
    setText('')
    setFileName('')
    setSheet(null)
    setDocFile(null)
    reset()
  }

  const takeFile = async (file: File) => {
    setError('')
    // Said here as well as on the server, so the person finds out before they
    // wait for an upload rather than after.
    if (!/\.(csv|xlsx)$/i.test(file.name)) {
      const ext = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')) : 'no extension'
      // The old binary .xls is a different format wearing a similar name, so it
      // gets the instruction that actually helps rather than the general one.
      const advice = /\.xls$/i.test(file.name)
        ? 'That is the older Excel format. Open it and use Save As to make a .xlsx or a .csv.'
        : 'Mass upload takes a .csv or a .xlsx. Paste the rows into the box below, or switch ' +
          'to “Document” above if this file holds written job descriptions.'
      setError(`“${file.name}” is ${ext}. ${advice}`)
      setFileName('')
      setText('')
      setSheet(null)
      setJob(null)
      return
    }
    setFileName(file.name)
    setJob(null)
    // An Excel file is bytes and is sent as it is, for the server to read. A
    // `.csv` is text and is read here, which is what lets a person paste rows
    // and edit them in the box before checking.
    if (/\.xlsx$/i.test(file.name)) {
      setSheet(file)
      setText('')
      return
    }
    setSheet(null)
    try {
      setText(await file.text())
    } catch {
      setError(`Could not read ${file.name}. Save it as a .csv file and try again.`)
    }
  }

  const takeDoc = (file: File) => {
    setError('')
    if (/\.csv$/i.test(file.name)) {
      setError(
        `“${file.name}” is a spreadsheet. Switch to “Spreadsheet” above, which reads every ` +
        'row exactly as written and is faster.',
      )
      setDocFile(null)
      return
    }
    if (!DOC_TYPES.test(file.name)) {
      const ext = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')) : 'no extension'
      setError(
        `A document upload takes a .pdf, .docx or .txt file, and “${file.name}” is ${ext}. ` +
        'Older .doc files cannot be read, so save it as .docx or as a PDF first.',
      )
      setDocFile(null)
      return
    }
    setDocFile(file)
    setJob(null)
  }

  const check = async () => {
    setChecking(true)
    setError('')
    setJob(null)
    setProgress('')
    try {
      const started = await api.startBulkCheck(text, fileName, sheet)
      // Checking happens away from the request: the file is split, then each
      // batch of rows is checked on its own. So the screen waits for the
      // verdicts rather than drawing whatever is there the instant it asks,
      // which would always be nothing.
      setJob(await waitForRows(started.id, CHECK_TIMEOUT_MS))
    } catch (err) {
      const message = err instanceof Error ? err.message : 'That file could not be checked.'
      setError(message)
      toast.fail(message)
    } finally {
      setChecking(false)
    }
  }

  const read = async () => {
    if (!docFile) return
    setChecking(true)
    setError('')
    setJob(null)
    setProgress('Uploading the document…')
    try {
      const started = await api.startDocsetCheck(docFile)
      setProgress('Reading the document. This takes about half a minute per twenty roles.')
      const done = await waitForRows(started.id, READ_TIMEOUT_MS, (j) => {
        const d = j.docset
        if (d?.pagesTotal) {
          setProgress(
            d.pagesDone
              ? `Read ${d.pagesDone} of ${d.pagesTotal} roles…`
              : `Found ${d.pagesTotal} roles. Reading them now…`,
          )
        }
      })
      setJob(done)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'That document could not be read.'
      setError(message)
      toast.fail(message)
    } finally {
      setChecking(false)
      setProgress('')
    }
  }

  /**
   * Fills a gap the document left, on every role that has it.
   *
   * Almost no job description document states a job family or a level, so
   * without this the whole file arrives held back and there is nothing on
   * screen to do about it.
   */
  const fillIn = async (patch: { family?: string; level?: string }) => {
    if (!job) return
    setFixing(true)
    try {
      setJob(await api.setOnRows(job.id, patch))
    } catch (err) {
      toast.fail(err instanceof Error ? err.message : 'That could not be set.')
    } finally {
      setFixing(false)
    }
  }

  /**
   * Saves one edited row.
   *
   * The whole job comes back re-checked, so a row a person has just corrected
   * turns from Skip to Ready in front of them rather than at import.
   */
  const saveRow = async (line: number, set: Record<string, string>, sections?: DocSection[]) => {
    if (!job) return
    setJob(await api.editRow(job.id, line, set, sections))
    setEditing(null)
  }

  const runImport = async () => {
    if (!job) return
    setImporting(true)
    setProgress('')
    try {
      // Driven to completion rather than asked once. A big file needs several
      // calls, because one request cannot outlast the gateway.
      const imported = await commitAll(job.id, (done, total) => {
        if (total > BATCHED_FROM) setProgress(`Imported ${done.toLocaleString()} of ${total.toLocaleString()}…`)
      })
      toast.ok(`Imported ${imported.toLocaleString()} job description${imported === 1 ? '' : 's'}`)
      // The company's role count lives on the session, which is read once at
      // sign in. Without this, importing five thousand roles left the sidebar
      // saying "0 job descriptions" underneath a library listing all of them.
      await refresh().catch(() => {})
      navigate('/job-descriptions')
    } catch (err) {
      toast.fail(err instanceof Error ? err.message : 'The import did not finish.')
    } finally {
      setImporting(false)
      setProgress('')
    }
  }

  return (
    <div className="screen screen--bulk" style={{ paddingTop: 28 }}>
      <BackButton to="all job descriptions" onClick={() => navigate('/job-descriptions')} />

      <div>
        <h1 className="page page--sub">Mass upload</h1>
        <p className="subline">
          Bring your whole library in at once. Check what was read, then import it. Nothing is
          saved until you do.
        </p>
      </div>

      <div className="seg" role="tablist" aria-label="What you are uploading">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'csv'}
          className={`seg__btn${mode === 'csv' ? ' seg__btn--on' : ''}`}
          onClick={() => switchTo('csv')}
        >
          Spreadsheet
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'doc'}
          className={`seg__btn${mode === 'doc' ? ' seg__btn--on' : ''}`}
          onClick={() => switchTo('doc')}
        >
          Document
        </button>
      </div>

      {mode === 'csv' ? (
        <div className="card card--pad" style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 18 }}>
          <div className="actions">
            <label className="file-btn">
              ↑ Choose a CSV file
              <input
                type="file"
                accept=".csv,.xlsx"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) void takeFile(f)
                  e.target.value = ''
                }}
              />
            </label>
            {fileName && <span className="hint">Loaded {fileName}</span>}
          </div>

          <label className="field">
            <span>Or paste rows, one job description per line</span>
            <textarea
              className="mono"
              rows={8}
              value={text}
              onChange={(e) => {
                setText(e.target.value)
                setJob(null)
              }}
              placeholder="title,family,level,location,payMin,payMax,summary,responsibilities,skills,qualifications,status"
            />
          </label>

          <div className="note">
            <b>Required columns</b>
            title, family, level, location, payMin, payMax, summary, responsibilities, skills,
            qualifications, status
            <br />
            A header row is detected and skipped. Separate several responsibilities, skills or
            qualifications with a pipe (|). Wrap any field containing a comma in double quotes. Pay
            values are in thousands. Level is one of Junior, Mid, Senior, Staff or Principal, and
            status is Draft, In review or Published, defaulting to Published.
          </div>

          <div className="actions">
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void check()}
              disabled={checking || (!text.trim() && !sheet)}
            >
              {checking ? 'Checking…' : 'Preview rows'}
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setText(SAMPLE_BULK_CSV)
                setFileName('sample-job-descriptions.csv')
                setJob(null)
              }}
            >
              Use an example
            </button>
            {/* A developer affordance for checking the preview at full size. It
                is deliberately absent when the app is talking to a real backend:
                it sat three clicks from Import and put 3,775 invented roles into
                a live library. */}
            {isMock && (
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setText(bigSampleCsv())
                  setFileName('five-thousand-rows.csv')
                  setJob(null)
                }}
                title="Loads 5,000 valid rows so the preview can be checked at full size"
              >
                Use a 5,000 row file
              </button>
            )}
            {job && <span className="hint">{summaryLine(ready, flagged)}</span>}
          </div>
        </div>
      ) : (
        <div className="card card--pad" style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 18 }}>
          <div className="actions">
            <label className="file-btn">
              ↑ Choose a PDF or Word file
              <input
                type="file"
                accept=".pdf,.docx,.txt"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) takeDoc(f)
                  e.target.value = ''
                }}
              />
            </label>
            {docFile && <span className="hint">Loaded {docFile.name}</span>}
          </div>

          <div className="note">
            <b>How this works</b>
            Upload the file you already have: a PDF or Word document with one job description per
            page. Each one is read into a role for you to check.
            <br />
            The words come from your document. A job family, a level and an industry are worked
            out from what each role actually says, marked as worked out, and can be changed on any
            row. Pay ranges and locations are never guessed, because those are facts rather than
            judgements.
          </div>

          <div className="actions">
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void read()}
              disabled={checking || !docFile}
            >
              {checking ? 'Reading…' : 'Read the document'}
            </button>
            {progress && <span className="hint" aria-live="polite">{progress}</span>}
            {job && !checking && (
              <span className="hint">{summaryLine(ready, flagged)}</span>
            )}
          </div>
        </div>
      )}

      {error && <ErrorState message={error} />}

      {job && (
        <div className="card card--flush">
          {/* What we decided the document was, before any row. A levelling grid
              is a thing customers really do upload, and it cannot become good
              job descriptions. Saying so is better than quietly producing
              thin ones. */}
          {job.docset && (
            <div className={`docset-kind${job.docset.kind === 'job_descriptions' ? '' : ' docset-kind--warn'}`}>
              <b>
                {job.docset.kind === 'job_descriptions'
                  ? 'This reads as written job descriptions.'
                  : job.docset.kind === 'level_matrix'
                    ? 'This reads as a levelling grid rather than written job descriptions.'
                    : 'This does not read as job descriptions.'}
              </b>{' '}
              {job.docset.note}
              {job.docset.kind !== 'job_descriptions' && (
                <>
                  {' '}
                  Roles taken from it have no written summary, because the document does not
                  contain one. You can still import them and write the summaries yourself.
                </>
              )}
            </div>
          )}

          {isDoc && (missingFamily > 0 || missingLevel > 0) && (
            <div className="fill-in">
              <div>
                <b>
                  {missingFamily > 0 && `${missingFamily} role${missingFamily === 1 ? '' : 's'} without a job family`}
                  {missingFamily > 0 && missingLevel > 0 && ', '}
                  {missingLevel > 0 && `${missingLevel} without a level`}
                </b>
                <br />
                Nothing could be worked out for {missingFamily + missingLevel === 1 ? 'it' : 'these'}.
                Use Edit on a row to set one role at a time, or fill every role still missing one
                here.
              </div>
              <div className="fill-in__controls">
                {missingFamily > 0 && (
                  <label>
                    <span className="sr-only">Job family for every role without one</span>
                    <select
                      defaultValue=""
                      disabled={fixing}
                      onChange={(e) => { if (e.target.value) void fillIn({ family: e.target.value }) }}
                    >
                      <option value="">Set job family…</option>
                      {families.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </label>
                )}
                {missingLevel > 0 && (
                  <label>
                    <span className="sr-only">Level for every role without one</span>
                    <select
                      defaultValue=""
                      disabled={fixing}
                      onChange={(e) => { if (e.target.value) void fillIn({ level: e.target.value }) }}
                    >
                      <option value="">Set level…</option>
                      {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </label>
                )}
              </div>
            </div>
          )}

          <div className="bulk-summary">
            <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-4)' }}>
              {isDoc ? 'Roles read from' : 'Rows read from'} {job.fileName}
            </span>
            <span className="count" aria-live="polite">
              <b style={{ color: 'var(--ok-ink-deep)' }}>{count(ready)} ready to import</b>
              {flagged > 0 && (
                <>
                  {' · '}
                  <b style={{ color: 'var(--bad-ink-deep)' }}>
                    {count(flagged)} {flagged === 1 ? 'needs' : 'need'} attention
                  </b>
                </>
              )}
            </span>
          </div>

          {/* A read that came up short twice is said out loud. Losing roles
              quietly is the worst thing this screen can do: a person imports
              what they see and never learns the rest of the document existed. */}
          {job.docset?.missing ? (
            <p className="shortfall">
              <b>
                {job.docset.missing} role{job.docset.missing === 1 ? '' : 's'} could not be read
                {job.docset.unread?.length ? `: ${list(job.docset.unread.map((t) => `“${t}”`))}.` : '.'}
              </b>{' '}
              {job.docset.unread?.length
                ? `The document lists ${job.docset.missing === 1 ? 'it' : 'them'}, but ${job.docset.missing === 1 ? 'its' : 'their'} text did not come back when asked for by name. `
                : `The document appears to hold ${job.docset.claimed} but only ${rows.length} came back. `}
              Import these if you want them, then read the document again, or add the missing{' '}
              {job.docset.missing === 1 ? 'role' : 'roles'} one at a time.
            </p>
          ) : null}

          {job.docset?.dropped?.length ? (
            <p className="shortfall shortfall--calm">
              Left out because {job.docset.dropped.length === 1 ? 'it has' : 'they have'} no
              responsibilities or requirements of{' '}
              {job.docset.dropped.length === 1 ? 'its' : 'their'} own, so{' '}
              {job.docset.dropped.length === 1 ? 'it is' : 'they are'} a heading rather than a
              role: {job.docset.dropped.join(', ')}.
            </p>
          ) : null}

          {isDoc && workedOut > 0 && (
            <p className="legend">
              <i /> worked out from what the role says, not read from the document. Use Edit on a
              row to change one.
            </p>
          )}

          <div
            role="row"
            style={{
              display: 'grid',
              gridTemplateColumns: grid,
              gap: 12,
              padding: '9px 16px',
              borderBottom: '1px solid var(--line)',
              minWidth: isDoc ? 0 : 900,
              position: 'sticky',
              top: 0,
              background: 'var(--surface)',
              zIndex: 1,
            }}
          >
            {cells.map((c) => (
              <span
                key={c.key}
                role="columnheader"
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'var(--ink-4)',
                }}
              >
                {c.label}
              </span>
            ))}
          </div>

          {rows.length === 0 ? (
            <div className="state">
              <h2>{isDoc ? 'No job descriptions in that document' : 'No rows in that file'}</h2>
              <p>
                {isDoc
                  ? 'Nothing in the file read as a job description. Check it is the right document and try again.'
                  : 'The file has a header row but nothing under it. Add at least one job description and check again.'}
              </p>
            </div>
          ) : isDoc ? (
            /* Laid out normally rather than through the row window.
               The window gives every row a height worked out from the data
               before anything is drawn, which is what lets a 5,000 row
               spreadsheet scroll. A document row carries wrapping prose, and
               prose whose height is guessed is prose that gets covered by the
               next row when the guess is short. A document holds tens of
               roles, so the window buys nothing here and costs correctness. */
            <div
              className="bulk-scroll"
              role="table"
              aria-label={`${rows.length} roles read from ${job.fileName}`}
              aria-rowcount={rows.length}
            >
              {rows.map((r) => (
                <DocRow
                  key={r.line}
                  row={r}
                  grid={grid}
                  paged={paged}
                  onEdit={() => setEditing(r)}
                />
              ))}
            </div>
          ) : (
            <RowWindow
              items={rows}
              heightOf={heightOf}
              maxHeight={Math.min(560, Math.max(220, rows.length * ROW_BASE))}
              label={`${rows.length} rows read from ${job.fileName}`}
              render={(r) => (
                <PreviewRow row={r} grid={grid} onEdit={() => setEditing(r)} />
              )}
            />
          )}

          <div className="bulk-foot">
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void runImport()}
              disabled={importing || ready === 0 || job.status === 'imported'}
            >
              {importing
                ? 'Importing…'
                : job.status === 'imported'
                  ? 'Already imported'
                  : `Import ${count(ready)} job description${ready === 1 ? '' : 's'}`}
            </button>
            <button type="button" className="btn" onClick={() => navigate('/job-descriptions')}>
              Cancel
            </button>
            {importing && progress && (
              <span className="hint" aria-live="polite">{progress}</span>
            )}
            <span className="hint">
              Use Edit on any row to correct it before importing. Flagged rows are skipped, and
              importing twice does not create two copies.
            </span>
          </div>
        </div>
      )}

      {editing && (
        <EditRow
          row={editing}
          families={families}
          onSave={(set, sections) => saveRow(editing.line, set, sections)}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  )
}

/** A spreadsheet row. Fixed height, because the row window needs to know it. */
function PreviewRow({ row, grid, onEdit }: {
  row: BulkRow
  grid: string
  onEdit: () => void
}) {
  const bad = row.errors.length > 0
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: grid,
        gap: 12,
        padding: '9px 16px',
        alignItems: 'start',
        borderBottom: '1px solid var(--line-soft)',
        background: bad ? 'var(--bad-bg)' : 'transparent',
        height: '100%',
      }}
    >
      <span role="cell" style={{ fontSize: 12.5, color: 'var(--ink-5)', fontVariantNumeric: 'tabular-nums' }}>
        {row.line}
      </span>
      <span role="cell" style={{ minWidth: 0 }}>
        {/* One line, whatever its length. The whole title is in the tooltip and
            in the panel Edit opens. */}
        <span className="row-title" title={row.title || undefined}>
          {row.title || <em style={{ color: 'var(--ink-5)' }}>no title</em>}
        </span>
        {/* The reason sits directly under the title, never in a column that
            scrolls out of view, which is the whole point of the screen. */}
        {row.errors.map((e, i) => (
          <span className="row-errors" style={{ display: 'block' }} key={i}>
            {e}
          </span>
        ))}
        {/* A blank cell takes a default, which the column contract promises and
            the screen never used to mention. A job family that quietly became
            Software Engineering is wrong in a way nobody can see. */}
        {/* Terse, because a spreadsheet row's height has to be known before it
            is drawn and prose that wraps further than the guess gets covered by
            the next row. The sentence that says what to do about it is in the
            panel Edit opens. */}
        {row.guessed?.length ? (
          <span className="row-same">{list(row.guessed)} took a default</span>
        ) : null}
        {row.sameTitle ? (
          <span className="row-same">
            {row.sameTitle} other{row.sameTitle === 1 ? '' : 's'} with this title
          </span>
        ) : null}
      </span>
      <span role="cell" style={{ fontSize: 13, color: 'var(--ink-3)' }}>{row.family}</span>
      <span role="cell" style={{ fontSize: 13, color: 'var(--ink-3)' }}>{row.level}</span>
      <span role="cell" style={{ fontSize: 13, color: 'var(--ink-3)' }}>{row.location}</span>
      <span role="cell" style={{ fontSize: 13, color: 'var(--ink-3)', fontVariantNumeric: 'tabular-nums' }}>
        {rangeShort(row.payMin, row.payMax)}
      </span>
      <span role="cell" style={{ fontSize: 13, color: 'var(--ink-3)' }}>{row.status}</span>
      <span role="cell" className="doc-row__verdict">
        <span className={`mark ${bad ? 'mark--skip' : 'mark--ready'}`}>{bad ? 'Skip' : 'Ready'}</span>
        <button type="button" className="btn btn--sm doc-row__edit" onClick={onEdit}>
          Edit
        </button>
      </span>
    </div>
  )
}

/**
 * A role read out of a document.
 *
 * Two parts on purpose. The top line is the columns, so five roles can still
 * be scanned down. Anything the reader wants to say about the role goes on its
 * own full width line underneath, where it can wrap to whatever length it
 * needs: squeezed into the title column it was three words wide and forty
 * lines long.
 */
function DocRow({ row, grid, paged, onEdit }: {
  row: BulkRow
  grid: string
  paged: boolean
  onEdit: () => void
}) {
  const bad = row.errors.length > 0
  const guessed = new Set(row.guessed ?? [])
  // Said before import, because the role will show less of the document than
  // its neighbours do (FR-57b).
  const noSections = row.from === 'model' && !row.sections?.length

  // Amber means worked out, explained once above the table rather than tagged
  // on every field of every row. Nearly every document row has all three
  // worked out, so the tagged version was three badges and a sentence saying
  // the same thing, five times over.
  const value = (field: string, text: string) => (
    <span
      role="cell"
      className={`doc-row__value${guessed.has(field) ? ' doc-row__value--worked' : ''}`}
      title={guessed.has(field) ? `${field} was worked out from what the role says` : undefined}
    >
      {text || <em style={{ color: 'var(--ink-5)' }}>not stated</em>}
    </span>
  )

  return (
    <div className={`doc-row${bad ? ' doc-row--bad' : ''}`} role="row">
      <div className="doc-row__top" style={{ gridTemplateColumns: grid }}>
        <span role="cell" className="doc-row__ord">{paged ? row.page : row.line}</span>
        <span role="cell" className="doc-row__title">
          {row.title || <em style={{ color: 'var(--ink-5)' }}>no title</em>}
        </span>
        {value('family', row.family)}
        {value('level', row.level)}
        {value('industry', row.industry ?? '')}
        <span role="cell" className="doc-row__verdict">
          <span className={`mark ${bad ? 'mark--skip' : 'mark--ready'}`}>{bad ? 'Skip' : 'Ready'}</span>
          <button type="button" className="btn btn--sm doc-row__edit" onClick={onEdit}>
            Edit
          </button>
        </span>
      </div>

      {(bad || row.notes || row.sameTitle || noSections) && (
        <div className="doc-row__detail">
          {/* The reason a row cannot import comes first, because it is the
              only thing on the row that stops somebody. */}
          {row.errors.map((e, i) => (
            <p className="doc-row__error" key={i}>{e}</p>
          ))}
          {/* Said, not refused. Two roles may share a name, and this is now
              the only warning that a document has been imported before. */}
          {row.sameTitle ? (
            <p className="doc-row__same">
              {row.sameTitle === 1
                ? 'One job description already has this title.'
                : `${row.sameTitle} job descriptions already have this title.`}{' '}
              This will be saved as another one.
            </p>
          ) : null}
          {noSections && (
            <p className="doc-row__same">
              Its sections could not be copied from the document, so it shows only the summary,
              responsibilities, skills and qualifications picked out of it. Read the document again
              to keep every section.
            </p>
          )}
          {row.notes && <p className="doc-row__note">{row.notes}</p>}
        </div>
      )}
    </div>
  )
}

/**
 * Correcting one role before it is imported.
 *
 * The one form every role is edited in (FR-47, FR-58): the key details, then
 * the job description section by section. A row read from a document shows
 * the document's own sections; a spreadsheet row shows sections built from
 * its columns (FR-61). It opens over the preview rather than expanding a row,
 * because a whole job description does not fit in a table cell.
 *
 * One panel, whose middle scrolls and whose buttons sit below it rather than
 * over it, so nothing of the form shows beneath them. The list behind does not
 * scroll while it is open (FR-62).
 */
function EditRow({ row, families, onSave, onCancel }: {
  row: BulkRow
  families: string[]
  onSave: (set: Record<string, string>, sections?: DocSection[]) => Promise<void>
  onCancel: () => void
}) {
  const [form, setForm] = useState<RoleFormState>(() => formFromRow(row))
  const [saving, setSaving] = useState(false)
  const [problem, setProblem] = useState('')

  const save = async () => {
    const wrong = formProblem(form)
    if (wrong) {
      setProblem(wrong)
      return
    }
    setProblem('')
    setSaving(true)
    try {
      const body = rowEditBody(row, form)
      await onSave(body.set, body.sections)
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'That could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label={`Edit ${row.title}`}>
      <div className="sheet__panel">
        <div className="sheet__head">
          <div>
            <h2>{row.title || 'Untitled role'}</h2>
            <p className="hint">
              {row.from === 'model'
                ? 'Read from the document. Change anything that is wrong, and this role alone changes.'
                : 'Read from your file. Change anything that is wrong, and this row alone changes.'}
            </p>
          </div>
          <button type="button" className="btn" onClick={onCancel}>Close</button>
        </div>

        <div className="sheet__body">
          {row.guessed?.length ? (
            <div className="note">
              {row.from === 'model' ? (
                <>
                  <b>Worked out rather than read</b>
                  {list(row.guessed)} {row.guessed.length === 1 ? 'was' : 'were'} worked out from
                  what this role says, because the document does not state{' '}
                  {row.guessed.length === 1 ? 'it' : 'them'} outright. Change{' '}
                  {row.guessed.length === 1 ? 'it' : 'any of them'} here and it becomes yours rather
                  than the reader's judgement.
                </>
              ) : (
                <>
                  <b>Filled in with a default</b>
                  {list(row.guessed)} {row.guessed.length === 1 ? 'was' : 'were'} blank in your file,
                  so {row.guessed.length === 1 ? 'it' : 'they'} took a default. Set{' '}
                  {row.guessed.length === 1 ? 'it' : 'them'} here if the default is wrong.
                </>
              )}
            </div>
          ) : null}

          <RoleForm draft={form} onChange={setForm} families={families} />
        </div>

        <div className="sheet__foot">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void save()}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save this role'}
          </button>
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
          {problem && <span className="hint hint--bad" role="alert">{problem}</span>}
        </div>
      </div>
    </div>
  )
}
